import React, { useCallback } from 'react'
import { NUMBER_OF_LESSONS } from '@mc/constants'
import { Badge, Button, Modal, ModalContent } from '@mc/design-system'
import { isMobile } from '@mc/devices'
import { usePrimaryCta, useAnnualPass } from '@mc/growth'
import { useTranslation } from '@mc/i18n'
import {
  trackBogoCtaClicked,
  trackBogoPromoModalCtaClicked,
  trackBogoPromoModalTermsClicked,
} from '@mc/pre-paywall-events'
import { BogoTermsLink, UrgencyTextOrange } from '../../featureVariables'
import { BOGO_I18N_NAMESPACE } from '../../i18n'
import { BogoOfferModal } from '../../types/bogoOfferModal'
import { trackOfferAcknowledged } from '../../utils/events'
import { CloseButton } from './CloseButton'
import { ContentItem } from './ContentItem'
import classes from './BogoPromoModal.module.scss'

export const BogoPromoModalContent = ({
  show,
  closeModal,
  setShow,
}: BogoOfferModal) => {
  const { t } = useTranslation(BOGO_I18N_NAMESPACE)
  const { ctaHref, ctaCopy } = usePrimaryCta()
  const annualPass = useAnnualPass()

  const onCtaClicked = useCallback(() => {
    trackOfferAcknowledged('offer_modal')
    trackBogoCtaClicked({
      action: ctaCopy,
      href: ctaHref,
      location: 'promotion-modal',
      annualPass,
    })
    trackBogoPromoModalCtaClicked(ctaCopy)
  }, [ctaCopy, ctaHref, annualPass])

  return (
    <Modal
      size='medium'
      canClickOutsideToClose={!isMobile()}
      show={show}
      onClose={closeModal}
      className={classes.homepageBogoOfferModal}
    >
      <ModalContent className='mc-invert mc-text-color--dark mc-corners--md mc-overflow--hidden'>
        <div className='mc-theme-light mc-bg-light d-flex flex-column mc-text--center justify-content-center mc-pb-8'>
          <CloseButton setShow={setShow} />

          <div className='mc-mt-8'>
            <Badge
              kind='dim'
              className={`${classes.badge} mc-text-color--dark mc-text--bold mc-text--uppercase`}
            >
              <UrgencyTextOrange />
            </Badge>
          </div>

          <h1 className='d-none d-lg-block mc-my-8 mc-text-h4 mc-px-10'>
            {t('modal.title')}
          </h1>
          <h1 className='d-block d-lg-none mc-my-8 mc-text-h3 mc-px-12'>
            {t('modal.title')}
          </h1>

          <ContentItem
            titleKey='modal.texts.text1.title'
            descriptionText={t('modal.texts.text1.description', {
              numberOfLessons: NUMBER_OF_LESSONS,
            })}
          />

          <ContentItem
            titleKey='modal.texts.text2.title'
            descriptionText={t('modal.texts.text2.description')}
          />

          <ContentItem
            titleKey='modal.texts.text3.title'
            descriptionText={t('modal.texts.text3.description')}
          />

          <p className='mc-text--normal mc-text-color--gray-400 mc-mb-4 mc-text-h7'>
            <BogoTermsLink
              textFixed={t('common.terms')}
              onClick={(text) => trackBogoPromoModalTermsClicked(text)}
            />
          </p>

          <div className='mc-px-8'>
            <Button
              kind='primary'
              render={<a href={ctaHref} />}
              onClick={onCtaClicked}
              fullWidth
            >
              {ctaCopy}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
